import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';
import { LookupRefService } from '@shared/services/lookup-ref.service';
import { L10nTranslationService } from 'angular-l10n';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter, ReplaySubject } from 'rxjs';
import { DefaultSelectionValue, FilterInputType, FormGroupValue, InputPropOption, InputProps } from '../input-props';
import { MatOptionSelectionChange } from '@angular/material/core';

@UntilDestroy()
@Component({
  selector: 'miq-simple-select',
  templateUrl: './simple-select.component.html',
  styleUrls: ['./simple-select.component.scss'],
  standalone: false,
})
export class SimpleSelectComponent<T = DefaultSelectionValue> implements OnInit, OnDestroy, OnChanges {
  @Input() props: InputProps<T>;
  @Input() value: T;
  @Input() multi = false;
  @Input() optionsEndpoint = null;
  @Input() hideTitle = false;
  @Input() disabled = false;

  @Output() selection: EventEmitter<FormGroupValue<any>> = new EventEmitter();

  mainValueControl: UntypedFormControl;
  optionsFilterControl: UntypedFormControl = new UntypedFormControl();
  filteredOptions: ReplaySubject<InputPropOption[]> = new ReplaySubject<InputPropOption[]>(1);

  dataKey = 'key';
  displayClearButton = false;
  isAllSelected = false;

  // Used to have a quick access to the options that come from a request
  // like when configuring the lookupCategory
  // and having the All selection configured
  #refinedOptions: InputPropOption[] = [];
  set refinedOptions(options: InputPropOption[]) {
    this.#refinedOptions = [...options];
  }
  get refinedOptions() {
    return this.#refinedOptions;
  }

  constructor(
    private lookupRefService: LookupRefService,
    public translationService: L10nTranslationService
  ) {}

  ngOnInit() {
    this.dataKey = this.props.dataKey || this.dataKey;
    this.displayClearButton = !!(Array.isArray(this.value) && this.value.length);
    this.mainValueControl = new UntypedFormControl(
      { value: this.value, disabled: this.disabled },
      this.props.controlOptions || {}
    );
    this.optionsFilterControl.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe((filterValue: string) => this.filterOptions(filterValue));

    if (this.props.lookupCategory) {
      this.setOptionsFromLookupCategory();
    } else if (this.optionsEndpoint) {
      this.setOptionsFromEndpoint();
    } else {
      this.setOptionsFromProps();
    }

    if (this.isAllSelected) {
      this.toggleAll();
    }
  }

  private setOptionsFromProps() {
    if (this.props.options && this.props.options.length > 0 && this.props.options[0].key === this.props.allKey) {
      this.props.options.shift();
    }
    if (Array.isArray(this.value) && this.value.length > 0) {
      if (
        this.value[0] === this.props.allKey ||
        (this.value.length === this.props.options.length && this.props.allKey)
      ) {
        this.isAllSelected = true;
      }
    }
    this.filteredOptions.next(this.props.options && this.props.options.slice());
  }

  private setOptionsFromEndpoint() {
    this.optionsEndpoint.pipe(untilDestroyed(this)).subscribe((options: any[]) => {
      if (this.props.field === 'subtypes') {
        this.refineOptions(options);
      } else {
        this.setDependantFieldOptions(options);
      }
    });
  }

  private setOptionsFromLookupCategory() {
    this.lookupRefService
      .loadDataFor(this.props.lookupCategory)
      .pipe(untilDestroyed(this))
      .subscribe((options: any[]) => {
        this.refineOptions(options);
      });
  }

  // (JALG) Check if there was a change in props to assign the options again
  ngOnChanges(changes: SimpleChanges) {
    if (changes.props && this.props && this.props.options) {
      this.filteredOptions.next(this.props.options.slice());
    }
    if (changes.optionsEndpoint && !changes.optionsEndpoint.firstChange && this.optionsEndpoint) {
      this.filteredOptions.next([]);
      this.mainValueControl.setValue(this.value);
      this.optionsEndpoint.pipe(untilDestroyed(this), filter(Boolean)).subscribe((options: any[]) => {
        if (this.props?.lookupPayload) {
          this.refineOptions(options);
        } else {
          this.setDependantFieldOptions(options);
        }
      });
    }

    // ! Note(Danny): Removed disabled from template and applied to formControl instead to avoid warning from formControl.
    if (!this.mainValueControl) {
      return;
    }
    if (this.disabled) {
      this.mainValueControl.disable();
      return;
    }
    this.mainValueControl.enable();
  }

  ngOnDestroy() {
    /* REQUIRED for untilDestroyed */
  }

  onSelectionChanged(event: MatSelectChange): void {
    // Keep current behavior if the component is not configured to use the Search input
    // if it is then the value changes are handle by the onOptionSelected function
    if (!this.props.hasFilter && this.props.type === FilterInputType.MultiSelect && this.props.allKey) {
      const options = this.props.options != null ? this.props.options : this.refinedOptions;
      const isAllSelected =
        event.value[0] === this.props.allKey || (event.value.length === options.length && !this.isAllSelected);
      if (isAllSelected !== this.isAllSelected) {
        this.isAllSelected = isAllSelected;
        this.toggleAll();
      } else if (isAllSelected) {
        (event.value as any[]).shift();
        this.isAllSelected = false;
        this.mainValueControl.setValue(event.value);
      }
    }

    if (this.props.type !== FilterInputType.MultiSelect) {
      this.emitSelection({ [this.props.field]: event.value });
    }
    this.displayClearButton = !!(Array.isArray(event.value) && event.value.length);
  }

  openedChange(event: boolean): void {
    if (!event) {
      let value = this.mainValueControl.value;
      if (this.isAllSelected || (!!value && value[0] === this.props.allKey)) {
        /**
         * * 'All' option string is only for UI purposes and should not be used in the filtering request...
         * * 'All' option equals to not filters at all...
         */
        value = [];
      }
      this.emitSelection({ [this.props.field]: value, isAll: this.isAllSelected });
      this.clearSearchBox();
    }
  }

  clearFilter() {
    if (Array.isArray(this.mainValueControl.value)) {
      this.mainValueControl.setValue([]);
      this.isAllSelected = false;
      this.emitSelection({ [this.props.field]: [], isAll: this.isAllSelected });
    }
    this.displayClearButton = false;
  }

  /**
   * Filters out the select options depending on a string input and assigns them to
   * the {@see filteredOptions} ReplaySubject.
   */
  private filterOptions(filterValue: string): void {
    if (!this.props.options) {
      return;
    }
    let search = filterValue;
    if (!search) {
      this.filteredOptions.next(this.props.options.slice());
    } else {
      search = search.toLowerCase();
      this.filteredOptions.next(
        this.props.options.filter(option => (option.value || option.key).toLowerCase().indexOf(search) > -1)
      );
    }
  }

  private setDependantFieldOptions(options: any[]) {
    if (options && options.length) {
      // geo api returns with a default option as All. Here we are making it as default selection.
      options[0].name = options[0].value = this.translationService.translate('recordsClipboard.select');
      options[0].key = null;
      const finalOptions = [...options];
      if (this.props.addNoneOption != null) {
        finalOptions.unshift(this.props.addNoneOption);
      }
      this.filteredOptions.next(finalOptions);
    }
  }

  private toggleAll(): void {
    if (this.isAllSelected) {
      const options = this.props.options != null ? this.props.options : this.#refinedOptions;
      this.mainValueControl.setValue([this.props.allKey, ...options.map(opt => opt.key)]);
    } else {
      this.mainValueControl.setValue([]);
    }
  }

  private emitSelection(value: FormGroupValue<any>): void {
    this.selection.emit(value);
  }

  private refineOptions(options) {
    const finalOptions = [...options];
    if (this.props.addNoneOption != null) {
      finalOptions.unshift(this.props.addNoneOption);
    }
    if (finalOptions.length > 0 && finalOptions[0].key === this.props.allKey) {
      finalOptions.shift();
    }
    if (Array.isArray(this.value) && Array.isArray(this.props.options) && this.value.length > 0) {
      if (
        this.value[0] === this.props.allKey ||
        (this.value.length === this.props.options.length && this.props.allKey)
      ) {
        this.isAllSelected = true;
      }
    }
    //to support filter the options
    if (this.props?.lookupPayload) {
      this.props.options = finalOptions;
    }
    this.filteredOptions.next(finalOptions);
    this.refinedOptions = finalOptions;
  }

  /**
   * When an option of the Mat-Select is selected/unselected
   * update the mainValueControl manually only if the configuration is set to use the search filter
   * and is allowed to select more than one item
   * @param event MatOptionSelectionChange
   */
  onOptionSelected({ source, isUserInput }: MatOptionSelectionChange) {
    // Avoid the loop of updating the view so this function will only be called when a user makes the change
    if (!isUserInput || this.props.type !== FilterInputType.MultiSelect || !this.props.hasFilter) {
      return;
    }
    const currentSelection = this.mainValueControl.value || [];
    const newVal: string[] = source.selected
      ? [...currentSelection, source.value]
      : currentSelection.filter((e: string) => e !== source.value);

    this.mainValueControl.setValue(newVal, { onlySelf: true, emitEvent: false });

    // Show/Hide the clear icon in the main select
    this.displayClearButton = newVal.length > 0;
  }

  /**
   * Clear the search text
   */
  clearSearchBox() {
    this.optionsFilterControl.setValue('');
  }
}
